/**
 * EPUB Loader
 *
 * Extracts text content from EPUB files using JSZip.
 * EPUB = ZIP archive containing XHTML chapters + metadata.
 */

import JSZip from 'jszip'
import type { GazeWeaveDocument, Paragraph, ParagraphType } from '@/types'

let paragraphCounter = 1000 // offset from content-loader IDs

function nextId(): string {
  return `epub-${++paragraphCounter}`
}

function makeParagraph(text: string, type: ParagraphType = 'body'): Paragraph {
  return {
    id: nextId(),
    text: text.trim(),
    type,
    fontSize: type === 'heading' ? 28 : 18,
    lineHeight: type === 'heading' ? 38 : 28,
  }
}

/**
 * Load and parse an EPUB file.
 */
export async function loadEpub(file: File): Promise<GazeWeaveDocument> {
  const zip = await JSZip.loadAsync(file)

  // 1. Find container.xml to locate the OPF file
  const containerXml = await zip.file('META-INF/container.xml')?.async('text')
  if (!containerXml) throw new Error('Invalid EPUB: missing container.xml')

  const opfPath = extractAttribute(containerXml, 'rootfile', 'full-path')
  if (!opfPath) throw new Error('Invalid EPUB: no rootfile path')

  // 2. Parse OPF to get spine order and metadata
  const opfContent = await zip.file(opfPath)?.async('text')
  if (!opfContent) throw new Error('Invalid EPUB: missing OPF file')

  const opfDir = opfPath.includes('/') ? opfPath.substring(0, opfPath.lastIndexOf('/') + 1) : ''

  // Extract title and author from metadata
  const title = extractTagContent(opfContent, 'dc:title') ?? file.name.replace(/\.epub$/i, '')
  const author = extractTagContent(opfContent, 'dc:creator') ?? undefined

  // Get manifest items (id → href mapping) — attributes can appear in any order
  const manifest = new Map<string, string>()
  const itemMatches = opfContent.matchAll(/<item\s([^>]*)\/?\s*>/g)
  for (const m of itemMatches) {
    const attrs = m[1]!
    const id = attrs.match(/id="([^"]*)"/)
    const href = attrs.match(/href="([^"]*)"/)
    if (id && href) {
      manifest.set(id[1]!, href[1]!)
    }
  }

  // Get spine order (list of itemref idrefs)
  const spineMatches = opfContent.matchAll(/<itemref\s[^>]*idref="([^"]*)"[^>]*\/?\s*>/g)
  const spineIds: string[] = []
  for (const m of spineMatches) {
    spineIds.push(m[1]!)
  }

  // 3. Extract text from each chapter in spine order
  const paragraphs: Paragraph[] = []

  for (const id of spineIds) {
    const href = manifest.get(id)
    if (!href) continue

    const fullPath = opfDir + href
    const chapterContent = await zip.file(fullPath)?.async('text')
    if (!chapterContent) continue

    // Parse XHTML to extract text paragraphs
    const chapterParagraphs = parseXHTML(chapterContent)
    paragraphs.push(...chapterParagraphs)
  }

  if (paragraphs.length === 0) {
    throw new Error('Could not extract any text from this EPUB')
  }

  return {
    title,
    author,
    source: 'epub',
    paragraphs,
    loadedAt: Date.now(),
  }
}

/**
 * Parse XHTML content into paragraphs.
 */
function parseXHTML(html: string): Paragraph[] {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'application/xhtml+xml')
  const paragraphs: Paragraph[] = []

  // Walk through content elements
  const walker = doc.createTreeWalker(
    doc.body || doc.documentElement,
    NodeFilter.SHOW_ELEMENT,
  )

  let node: Node | null = walker.currentNode
  const seen = new Set<string>()

  while (node) {
    if (node instanceof Element) {
      const tag = node.tagName.toLowerCase()
      const text = node.textContent?.trim()

      if (text && text.length > 1 && !seen.has(text)) {
        // Only process leaf-ish elements (avoid duplicating parent text)
        const hasBlockChildren = node.querySelector('p, h1, h2, h3, h4, h5, h6, div, blockquote, li')

        if (!hasBlockChildren) {
          seen.add(text)

          if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag)) {
            paragraphs.push(makeParagraph(text, 'heading'))
          } else if (tag === 'blockquote') {
            paragraphs.push(makeParagraph(text, 'quote'))
          } else if (tag === 'li') {
            paragraphs.push(makeParagraph(`• ${text}`, 'list-item'))
          } else if (['p', 'div', 'span', 'section', 'article'].includes(tag)) {
            paragraphs.push(makeParagraph(text, 'body'))
          }
        }
      }
    }
    node = walker.nextNode()
  }

  return paragraphs
}

// Simple XML helpers (avoid pulling in a full XML parser)
function extractAttribute(xml: string, tag: string, attr: string): string | null {
  const regex = new RegExp(`<${tag}[^>]*${attr}="([^"]*)"`, 'i')
  const match = xml.match(regex)
  return match ? match[1]! : null
}

function extractTagContent(xml: string, tag: string): string | null {
  const regex = new RegExp(`<${tag}[^>]*>([^<]*)</${tag}>`, 'i')
  const match = xml.match(regex)
  return match ? match[1]!.trim() : null
}
